import React from 'react';
import type { ImportRow, ImportRowExclusionHandlers } from '../../importRows';
import { RowExclusionLockContext } from '../rowExclusion';
import { ImportGrid } from './ImportGrid';

type ImportTableProps = ImportRowExclusionHandlers & {
    /** The rows to show, in file order. */
    rows: ImportRow[];
    /** Every row of the file, for the footer's "of m". */
    totalRows: number;
    /** What the table says with no row to show: the file has none, or the tab. */
    emptyMessage: string;
    /** A row's press (a click, or Enter on the focused row): opens it in the drawer. */
    onOpenRow: (rowNumber: number) => void;
    /** The import is running: no row opens and no exclusion changes. */
    isDisabled?: boolean;
};

/**
 * The file's rows as one compact read-only line each, on HeroUI Pro's
 * `DataGrid` in virtualized mode (`ImportGrid`; the chart of accounts is the
 * precedent): only the rows in the viewport are in the DOM, so 2,000 rows
 * scroll like twenty. The columns are built once per language and exclusion
 * handlers (stable: the store's actions); a row the hook kept (`buildRows`)
 * is the same object, so React Aria's collection reuses its rendering. A
 * row's controls are its Edit and Exclude / Include buttons and, on a
 * flagged row, its note's Fix link; pressing the row itself (a click, or
 * Enter on the focused row), its Edit or its Fix opens it in the drawer
 * (`onOpenRow`).
 *
 * While the import runs (`isDisabled`) rows do not open and the controls
 * wait, without a render of the grid: the row's press reads the state when
 * it happens, and the buttons read `RowExclusionLockContext`. As a grid prop
 * it rebuilt every row as the import started and again as it answered.
 *
 * The footer says how many rows the table shows (a tab's) of the file's
 * total.
 *
 * Memoized: the grid walks every row it holds on each render, so a render of
 * the step that leaves these props alone (a re-check's pending state, an
 * address change that is not the tab) skips it.
 */
export const ImportTable = React.memo<ImportTableProps>(function ImportTable({
    onOpenRow,
    isDisabled = false,
    ...grid
}) {
    const isDisabledRef = React.useRef(isDisabled);
    isDisabledRef.current = isDisabled;
    const onEditRow = React.useCallback(
        (rowNumber: number) => {
            if (!isDisabledRef.current) {
                onOpenRow(rowNumber);
            }
        },
        [onOpenRow],
    );
    const onRowAction = React.useCallback(
        (key: React.Key) => onEditRow(Number(key)),
        [onEditRow],
    );

    return (
        <RowExclusionLockContext.Provider value={isDisabled}>
            <ImportGrid
                {...grid}
                onEditRow={onEditRow}
                onRowAction={onRowAction}
            />
        </RowExclusionLockContext.Provider>
    );
});
